export interface TaxConversionOptions {
    /**
     * Tax rate the configurator was authored against (percent, e.g. `19`).
     * From `component.setting.taxRate`. Only matters when
     * {@link priceIncludesTax} is true.
     */
    configuratorTaxRate: number;
    /**
     * Whether prices stored in the component already include tax. When `true`
     * the converter strips configurator-tax first, then applies store-tax.
     */
    priceIncludesTax: boolean;
    /**
     * Tax rate of the embedding host shop (percent). When `undefined` the
     * function is the identity — used during standalone preview where no
     * host store is around to ask.
     */
    storeTaxRate: number | undefined;
}

/**
 * Convert an amount from the configurator's tax basis to the embedding host
 * shop's tax basis. Mirrors the legacy `Justselling_Configento_App_Tax.convert`
 * semantics:
 *
 *   - no store-tax known → identity (preview / unembedded use)
 *   - configurator prices include tax → strip configurator-tax then apply store-tax
 *   - configurator prices exclude tax → apply store-tax directly
 *
 * Pure function — no rounding side-effects baked in; callers format with
 * {@link formatCurrency} which clamps to 2 decimals.
 */
export function convertTax(amount: number, opts: TaxConversionOptions): number {
    if (opts.storeTaxRate === undefined || !Number.isFinite(opts.storeTaxRate)) {
        return amount;
    }
    const configuratorRate = Number.isFinite(opts.configuratorTaxRate)
        ? opts.configuratorTaxRate
        : 0;
    // Gross on both sides at the same rate: exactly the input. Skipping the
    // divide-and-multiply keeps the amount bit-identical (100, not
    // 100.00000000000001), which the server-side price check compares on.
    if (opts.priceIncludesTax && opts.storeTaxRate === configuratorRate) {
        return amount;
    }
    const net = opts.priceIncludesTax ? amount / (1 + configuratorRate / 100) : amount;
    return net * (1 + opts.storeTaxRate / 100);
}
